import { Container } from "@/components/ui/Container";
import { MotionWrapper } from "@/components/ui/MotionWrapper";
import { clients, type Client } from "@/content/clients";

// Carrossel (duas esteiras em loop infinito, sentidos opostos) — com ~140
// provedores reais não dá pra mostrar tudo em grade estática, e a maioria
// tem razão social longa demais pra caber num card. Cada card mostra a
// sigla em destaque (derivada automaticamente, ver src/content/clients.ts)
// e o nome completo como subtexto menor embaixo.

const half = Math.ceil(clients.length / 2);
const rowA = clients.slice(0, half);
const rowB = clients.slice(half);

function ClientCard({ client }: { client: Client }) {
  return (
    <div className="group relative flex w-48 shrink-0 flex-col overflow-hidden rounded-xl border border-line bg-bg-raised p-4 transition-colors duration-200 hover:border-accent-lime/50">
      {/* linha de scan no topo, corre no hover */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[2px] overflow-hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <div className="animate-card-scan h-full w-1/3 bg-gradient-to-r from-transparent via-accent-lime to-transparent" />
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute -inset-1 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: "radial-gradient(circle at 30% 20%, rgba(153,255,0,0.2), transparent 60%)" }}
      />

      <span aria-hidden className="absolute left-2 top-2 h-2.5 w-2.5 border-l border-t border-accent-lime opacity-0 transition-opacity duration-300 group-hover:opacity-70" />
      <span aria-hidden className="absolute bottom-2 right-2 h-2.5 w-2.5 border-b border-r border-accent-lime opacity-0 transition-opacity duration-300 group-hover:opacity-70" />

      <p className="relative truncate text-xl font-black italic tracking-tight text-text-primary transition-colors duration-200 group-hover:text-accent-lime">
        {client.short}
      </p>
      <p className="relative mt-1.5 line-clamp-2 text-[10px] leading-snug text-text-muted">
        {client.name}
      </p>
    </div>
  );
}

function MarqueeRow({ items, reverse = false }: { items: Client[]; reverse?: boolean }) {
  const track = [...items, ...items];
  return (
    <div className="group relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div className={`flex w-max gap-3 ${reverse ? "animate-marquee-slow-reverse" : "animate-marquee-slow"}`}>
        {track.map((client, i) => (
          <ClientCard key={`${client.name}-${i}`} client={client} />
        ))}
      </div>
    </div>
  );
}

export function ClientsSection() {
  return (
    <section id="clientes" className="relative scroll-mt-24 overflow-hidden py-20 sm:py-28">
      <div aria-hidden className="bg-grid bg-grid-drift pointer-events-none absolute inset-0 opacity-10" />

      <Container className="relative">
        <MotionWrapper className="mx-auto max-w-xl text-center">
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-accent-lime">
            Clientes
          </p>
          <h2 className="mt-3 text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-text-primary sm:text-5xl">
            Provedores que já operam{" "}
            <span
              className="text-accent-lime"
              style={{ textShadow: "0 0 24px rgba(153,255,0,0.35)" }}
            >
              com a SAT Labs.
            </span>
          </h2>
          <p className="mt-5 text-base text-text-secondary sm:text-lg">
            {clients.length}+ provedores em todo o Brasil — de pequenas operações regionais a
            redes multi-estado, todos na mesma infraestrutura.
          </p>
        </MotionWrapper>
      </Container>

      <MotionWrapper className="mt-14 space-y-4" delay={0.1}>
        <MarqueeRow items={rowA} />
        <MarqueeRow items={rowB} reverse />
      </MotionWrapper>
    </section>
  );
}
